import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import {
  Dashboard,
  DirectionsBoat,
  Group,
  Description,
  Receipt,
  BusinessCenter,
  Route,
  Settings,
  People,
  Business,
  UploadFile,
  LocalGasStation,
  Insights,
  PersonAdd,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { memo, useMemo, useCallback } from 'react';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

export const AdminSidebar = memo(({ open, onClose, isMobile }: AdminSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobile) {
        onClose();
      }
    },
    [navigate, isMobile, onClose]
  );

  const navigationItems = useMemo(
    () => [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'Users', icon: <People />, path: '/users' },
      { text: 'Entities', icon: <Business />, path: '/entities' },
      { text: 'Demo Accounts', icon: <PersonAdd />, path: '/demo-accounts' },
      { text: 'Vessels', icon: <DirectionsBoat />, path: '/vessels' },
      {
        text: 'Upload voyage spreadsheet',
        icon: <UploadFile />,
        path: '/vessel-voyage-xls-upload',
      },
      { text: 'Voyages', icon: <Route />, path: '/voyages' },
      { text: 'Charterers', icon: <Group />, path: '/charterers' },
      { text: 'Contracts', icon: <Description />, path: '/contracts' },
      { text: 'Invoices', icon: <Receipt />, path: '/invoices' },
      { text: 'Procurements', icon: <BusinessCenter />, path: '/procurements' },
      { text: 'Emission Factors', icon: <LocalGasStation />, path: '/emission-factors' },
      { text: 'Market Insights', icon: <Insights />, path: '/market-insights' },
    ],
    []
  );

  const settingsItems = useMemo(
    () => [{ text: 'Settings', icon: <Settings />, path: '/settings' }],
    []
  );

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Title Section */}
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
          ETS Navigator
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Admin Panel
        </Typography>
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 2, py: 3 }}>
          {navigationItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider />

        {/* Settings Section */}
        <List sx={{ px: 2, py: 2 }}>
          {settingsItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                slotProps={{
                  primary: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              top: '64px', // Below AppBar
              height: 'calc(100vh - 64px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
});
